import { ProductDto } from "@custom-types/products"
import { getDefaultValues } from "@features/administration/products/EditProduct/GraphTagsForm/getDefaultValues"
import { getFormSchema } from "@features/administration/products/EditProduct/GraphTagsForm/getFormSchema"
import { yupResolver } from "@hookform/resolvers/yup"
import * as yup from "yup"
import { useGetProductGraphTagsQuery } from "@store/services/productsService"
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from "react"
import {
  FormProvider,
  SubmitErrorHandler,
  SubmitHandler,
  useFieldArray,
  useForm,
} from "react-hook-form"
import {
  CircularProgress,
  Grid,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material"
import VirtualizedGraphTagRows from "@features/administration/products/EditProduct/GraphTagsForm/VirtualizedGraphTagRows"
import { downloadFile, readFile } from "@utils/file"
import {
  GRAPH_TAG_FILE_REQUIRED_HEADERS,
  formValuesToGraphTagsFileContent,
  getRowsFromGraphTagsFileContent,
} from "@features/administration/products/EditProduct/graphTagsHelper"
import { enqueueSnackbar } from "notistack"

export const GRAPH_TAGS_FORM_ID = "graph-tags-form"

type GraphTagsFormProps = {
  product: ProductDto
  onSubmit: SubmitHandler<GraphTagsFormValues>
  onSubmitError: SubmitErrorHandler<GraphTagsFormValues>
}

export type GraphTagsFormValues = yup.InferType<ReturnType<typeof getFormSchema>>
export type GraphTagRowFormValues = GraphTagsFormValues["rows"][number]

export type GraphTagsFormRef = {
  addTag: (newTag: GraphTagRowFormValues) => void
  exportTagsFile: () => void
  importTagsFile: (file: File) => void
}

const GraphTagsForm = forwardRef<GraphTagsFormRef, GraphTagsFormProps>((props, ref) => {
  const tableContainerRef = useRef<HTMLDivElement>(null)
  const { data: tagsResponse, isLoading } = useGetProductGraphTagsQuery(
    {
      key: props.product.key,
      orgKey: props.product.organisation_key,
      divKey: props.product.division_key,
    },
    {
      skip: props.product.graph_tags_s3_location === "",
    },
  )

  const formMethods = useForm<GraphTagsFormValues>({
    defaultValues: getDefaultValues(tagsResponse),
    resolver: yupResolver(getFormSchema()),
  })

  useEffect(() => {
    if (!tagsResponse) return

    formMethods.reset(getDefaultValues(tagsResponse))
  }, [formMethods, props.product, tagsResponse])

  const { fields, append, remove } = useFieldArray({ control: formMethods.control, name: "rows" })

  const handleAddTag = useCallback(
    (newTagRow: GraphTagsFormValues["rows"][number]) => {
      if (!tableContainerRef.current) return

      append(newTagRow)

      // scroll to the bottom of the table to show the newly added row
      tableContainerRef.current.scrollTo({
        top: tableContainerRef.current.scrollHeight,
        behavior: "smooth",
      })
    },
    [append],
  )

  const handleExportTags = useCallback(() => {
    const fileContent = formValuesToGraphTagsFileContent(formMethods.getValues())

    const blob = new Blob([fileContent], { type: "text/csv;charset=utf-8" })
    const blobUrl = URL.createObjectURL(blob)

    downloadFile(`graph-tags-${props.product.key}.csv`, blobUrl)
    URL.revokeObjectURL(blobUrl)
  }, [formMethods, props.product.key])

  const handleImportTagsFile = useCallback(
    async (file: File) => {
      const fileAsString = await readFile(file)
      const newRows = getRowsFromGraphTagsFileContent(fileAsString)

      if (newRows.length === 0) {
        enqueueSnackbar(
          `Invalid CSV file. Please check if given file has correct headers and values.
        Required headers are: ${GRAPH_TAG_FILE_REQUIRED_HEADERS.join(", ")}`,
          { variant: "error" },
        )
        return
      }

      formMethods.reset(getDefaultValues(fileAsString), { keepDefaultValues: false })
    },
    [formMethods],
  )

  // allow parent component to call callbacks that should have direct impact on the form state
  useImperativeHandle(
    ref,
    () => ({
      addTag: handleAddTag,
      exportTagsFile: handleExportTags,
      importTagsFile: handleImportTagsFile,
    }),
    [handleAddTag, handleExportTags, handleImportTagsFile],
  )

  if (isLoading) {
    return (
      <Grid container direction="column">
        <Grid item alignSelf="center">
          <CircularProgress />
        </Grid>
      </Grid>
    )
  }

  return (
    <FormProvider {...formMethods}>
      <form
        autoComplete="off"
        onSubmit={formMethods.handleSubmit(props.onSubmit, props.onSubmitError)}
        id={GRAPH_TAGS_FORM_ID}
      >
        <TableContainer
          ref={tableContainerRef}
          sx={{
            overflowX: "initial",
            overflowY: "auto",
            maxHeight: "calc(100dvh - 300px)",
          }}
        >
          <Table stickyHeader size="small" sx={{ tableLayout: "fixed" }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 20 }}>
                  <strong>No.</strong>
                </TableCell>
                <TableCell sx={{ width: 130 }}>
                  <strong>Name</strong>
                </TableCell>
                <TableCell sx={{ width: 200 }}>
                  <strong>Description</strong>
                </TableCell>
                <TableCell sx={{ width: 130 }}>
                  <strong>Rule</strong>
                </TableCell>
                <TableCell sx={{ width: 40 }}>
                  <strong>Actions</strong>
                </TableCell>
              </TableRow>
            </TableHead>

            <VirtualizedGraphTagRows
              tableContainerRef={tableContainerRef}
              fields={fields}
              onDelete={remove}
            />
          </Table>
        </TableContainer>
      </form>
    </FormProvider>
  )
})

GraphTagsForm.displayName = "GraphTagsForm"

export default GraphTagsForm
